/* ============================================================================
   product-recommendations.js — <sf-recommendations> (Sneak Freak · B8)
   ----------------------------------------------------------------------------
   Fetches sections/product-recommendations.liquid through the Product
   Recommendations API when the shell comes near the viewport and swaps the
   rendered rail in. Needs window.theme (theme.js) — fetchText / sectionHtml.

   Attributes
     data-url          recommendations URL (section_id, product_id, limit, intent)
     data-performed    present on the fetched render (never fetch again)
     data-design-mode  present in the theme editor (keep the shell visible)
     data-bag          the Bag page rail (BUILD bq): always asks the API (the Liquid stand-in never stops it),
                       drops data-exclude's handles (",a,b," — the bag's products) from whatever comes back,
                       tops a short API row up from the stand-in's cards, keeps data-limit cards, and follows the
                       bag on the page: a product added there leaves the rail, an empty bag hides it
   Classes
     .is-empty         no products rendered yet / none returned
     .is-loading       fetch in flight (aria-busy)
   ============================================================================ */
(function () {
  'use strict';
  if (customElements.get('sf-recommendations')) return;

  class SFRecommendations extends HTMLElement {
    connectedCallback() {
      this._ctrl = new AbortController();
      this._done = false;
      const bag = this.hasAttribute('data-bag');
      const theme = window.theme || {};
      if (bag && theme.pubsub && theme.pubsub.subscribe) {
        this._unsub = theme.pubsub.subscribe('cart:updated', (d) => {
          if (!d || !d.cart) return;
          if (typeof d.cart.item_count === 'number') this.hidden = d.cart.item_count === 0;
          /* the bag changed on the page (e.g. a card's quick-add): what is in it now leaves the rail */
          if (Array.isArray(d.cart.items)) {
            this.setAttribute('data-exclude', ',' + d.cart.items.map((i) => i.handle).join(',') + ',');
            this._prune(this.querySelector('sf-rail'));
          }
        });
      }
      const url = this.getAttribute('data-url');
      /* a Liquid stand-in rail of one or two cards is not a reason to stop: the API, or failing that the
         same-brand search below, can still fill the row out */
      const pre = this.querySelector('sf-rail');
      const preIsFull = !bag && pre && pre.children.length >= 3;
      if (!url || this.hasAttribute('data-performed') || preIsFull) return;

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this._io.disconnect();
              this._io = null;
              this._load(url);
            }
          },
          { rootMargin: '400px 0px' }
        );
        this._io.observe(this);
      } else {
        this._load(url);
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._ctrl) this._ctrl.abort();
      if (this._unsub) this._unsub();
    }

    /** Bag page only: drop the bag's own products from a rail, top a short one up from `spare` (the stand-in rail —
        its cards are bag-free already; the endpoint's collection top-up is not, so pruning can leave fewer cards
        than the stand-in showed), keep data-limit cards, one dot per card. */
    _prune(rail, spare) {
      if (!this.hasAttribute('data-bag') || !rail) return;
      const skip = this.getAttribute('data-exclude') || '';
      const handle = (card) => card.dataset && card.dataset.handle;
      Array.from(rail.children).forEach((card) => { const h = handle(card); if (h && skip.indexOf(',' + h + ',') !== -1) card.remove(); });
      const max = parseInt(this.getAttribute('data-limit'), 10) || 0;
      if (spare && spare !== rail && rail.children.length && max > 0) {
        const have = ',' + Array.from(rail.children).map(handle).join(',') + ',';
        Array.from(spare.children).forEach((card) => {
          const h = handle(card);
          if (rail.children.length >= max || !h || have.indexOf(',' + h + ',') !== -1 || skip.indexOf(',' + h + ',') !== -1) return;
          rail.appendChild(card.cloneNode(true));
        });
      }
      if (max > 0) while (rail.children.length > max) rail.lastElementChild.remove();
      const box = rail.parentElement;
      let dots = box && box.querySelector(':scope > .rail-dots');
      const n = rail.children.length;
      if (n < 2 || !box) { if (dots) dots.remove(); return; }
      if (!dots) {
        dots = document.createElement('div'); // same markup as snippets/rail-dots.liquid
        dots.className = 'rail-dots quick-finds__dots';
        dots.setAttribute('aria-hidden', 'true');
        box.appendChild(dots);
      }
      while (dots.children.length > n) dots.lastElementChild.remove();
      while (dots.children.length < n) {
        const dot = document.createElement('span');
        dot.className = 'rail-dots__dot dot-indicator' + (dots.children.length ? '' : ' is-active');
        dots.appendChild(dot);
      }
    }

    /** Same-brand stand-in: GET /search?section_id=brand-picks&q=vendor:<vendor> → rail of product cards (current product removed). */
    async _loadFallback() {
      const url = this.getAttribute('data-fallback-url');
      if (!url) return;
      try {
        const res = await fetch(url, { signal: this._ctrl.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) return;
        const html = await res.text();
        const theme = window.theme || {};
        const wrapper = theme.sectionHtml ? theme.sectionHtml(html, 'brand-picks') : new DOMParser().parseFromString(html, 'text/html').body;
        const rail = wrapper && wrapper.querySelector('sf-rail');
        if (!rail) return;
        const handle = this.getAttribute('data-handle');
        Array.from(rail.children).forEach((card) => { if (handle && card.dataset && card.dataset.handle === handle) card.remove(); });
        if (this.hasAttribute('data-bag')) this._prune(rail);
        else while (rail.children.length > 4) rail.lastElementChild.remove();
        const existing = this.querySelector('sf-rail');
        if (!rail.children.length || (existing && existing.children.length >= rail.children.length)) return;
        rail.className = 'rail quick-finds-rail';
        const head = document.createElement('div');
        head.className = 'container';
        const eyebrow = this.getAttribute('data-eyebrow') || '';
        const heading = this.getAttribute('data-heading') || '';
        head.innerHTML = '<div class="section-header also-like__header"><div class="section-header__text">' + (eyebrow ? '<p class="section-header__eyebrow"></p>' : '') + '<h2 class="section-header__title"></h2></div></div>';
        if (eyebrow) head.querySelector('.section-header__eyebrow').textContent = eyebrow;
        head.querySelector('.section-header__title').textContent = heading;
        const body = document.createElement('div');
        body.className = 'container container--flush';
        body.appendChild(rail);
        this.replaceChildren(head, body);
        this.classList.remove('is-empty');
        if (theme.initReveal) theme.initReveal(this);
      } catch (err) { /* keep the section empty */ }
    }

    async _load(url) {
      if (this._done) return;
      this._done = true;
      const theme = window.theme || {};
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(url, { signal: this._ctrl.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('recommendations ' + res.status);
        const html = await res.text();
        const id = this.getAttribute('data-section-id');
        const wrapper = theme.sectionHtml ? theme.sectionHtml(html, id) : new DOMParser().parseFromString(html, 'text/html').body;
        const source = wrapper && (wrapper.matches && wrapper.matches('sf-recommendations') ? wrapper : wrapper.querySelector('sf-recommendations'));
        const rail = source && source.querySelector('sf-rail');
        this._prune(rail, this.querySelector('sf-rail'));
        if (!source || !rail || !rail.children.length) {
          /* nothing from the API: keep the Liquid same-brand rail if present, otherwise fetch same-brand products */
          const own = this.querySelector('sf-rail');
          if (!own || own.children.length < 3) await this._loadFallback();
          if (!this.querySelector('sf-rail')) this.classList.add('is-empty');
          return;
        }
        this.replaceChildren(...Array.from(source.childNodes));
        this.classList.remove('is-empty');
        this.setAttribute('data-performed', 'true');
        if (theme.initReveal) theme.initReveal(this);
        if (theme.pubsub && theme.pubsub.publish) theme.pubsub.publish('recommendations:loaded', { sectionId: id, count: rail.children.length });
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        if (!this.querySelector('sf-rail')) this.classList.add('is-empty');
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('sf-recommendations', SFRecommendations);
})();
